import _ from 'lodash'
import uuid from 'uuid'
import pluralize from 'pluralize'
import { PureComponent } from 'react'
import { withApollo } from '@apollo/client/react/hoc'
import { connect } from 'react-redux'
import { message, Modal } from 'antd'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'

import Box from '~/core/Box'
import FileUploader from '~/core/FileUploader'
import {
    getNewQuoteIdForDropzone,
    setNewQuoteIdForDropzone,
    resetQuoteIdForDropzone,
} from '~/redux-modules/quotes'
import { getUserIdentity } from '~/redux-modules/identity'
import { uploadItem } from '~/lib/uploadFile'
import logger from '~/lib/logger'
import defer from '~/lib/defer'
import isFlagEnabled from '~/lib/isFlagEnabled'
import { CAD_EXTENSIONS } from '~/lib/cadExtensions'
import { isBlockedFileName } from '~/lib/fileExtensions'
import { UPLOAD_BLOCKED_ATTACHMENT_ERROR } from '~/lib/flashMsgs'
import { getFileExtension } from '~/lib/getFileExtension'
import customWithRouter from '~/lib/customWithRouter'

import UnitsSection from './UnitsSection'
import {
    createQuote,
    createQuoteModelRevision,
    createQuoteAttachmentRevision,
    finalizeUploadAndProcessPart,
    markQuoteAttachmentRevisionUploaded,
    CREATE_QUOTE_ERROR_MSG,
    updateGeneratedDrawing,
    markUploadedAttachment,
    isPdf,
    isValidDfmReportType,
    isValidDfmReportSize,
} from './logic'
import { grabAndLowerExt, getMeshExts } from './selectors'

const UPLOADER_PLACEMENT = 'project-uploader'
const UNITS_MM = 'mm'

export const UnitsModalContent = (
    meshFiles,
    onUnitsChange,
    stlDimensions,
    units,
) => (
    <Box marginTop="24px" direction="col">
        <UnitsSection
            meshFiles={meshFiles}
            onUnitsChange={onUnitsChange}
            dimensions={stlDimensions}
            units={units}
        />
    </Box>
)

class QuoteUploader extends PureComponent {
    state = {
        units: UNITS_MM,
        isCreatingQuote: false,
        isUploadingFiles: false,
        isRedirecting: false,
    }

    hasAskedForUnits = false
    resolved = false
    newQuoteId = null

    promiseChain = null

    checkMeshFiles = (file, files) => {
        const { promise, resolve } = defer()

        // Because the core/FileUploader calls `onFileDropped` for every file
        // we need to queue up the uploads and only continue if the user
        // clicks proceed.

        // The following code sets up this promise queue and adds the
        // cleanup code in the finally block
        if (this.promiseChain == null) {
            this.promiseChain = promise.finally(() => {
                // I'm using instance variables here because I don't
                // want to deal with the setState lifecycle
                this.promiseChain = null
                this.hasAskedForUnits = false
                this.resolved = false
            })
        }

        const meshFiles = getMeshExts(files)
        const hasMeshes = !_.isEmpty(meshFiles)

        // Ask the user for a units selection and either continue to upload
        // or cancel all the uploads by resolving `false`.
        // Also since this function runs once per file we check to make sure
        // we haven't opened the modal already
        if (
            hasMeshes &&
            !this.hasAskedForUnits &&
            !this.props.onSinglePdfAttachment
        ) {
            this.hasAskedForUnits = true
            const count = _.size(meshFiles)
            const title = `${count} mesh ${pluralize('file', count)} detected`

            Modal.info({
                title,
                okText: 'Process uploads',
                onOk: () => {
                    // Resolve in the next tick to allow for the units to propagate
                    // via setState
                    _.defer(() =>
                        resolve(this.identifyFileTypeAndCreate(file, files)),
                    )

                    return this.promiseChain
                },
                content: UnitsModalContent(meshFiles, this.onUnitsChange),
            })

            return this.promiseChain
        }

        // Resolve immediately if we did not detect mesh files
        if (
            (!hasMeshes && !this.resolved) ||
            this.props.onSinglePdfAttachment
        ) {
            this.resolved = true
            _.defer(() => resolve())
        }

        return this.promiseChain.then(
            // If the user clicked cancel, make sure we don't try to upload anything else
            shouldUpload =>
                shouldUpload !== false &&
                this.identifyFileTypeAndCreate(file, files),
        )
    }
    onBeforeUpload = async (file, files) => {
        const { id, dispatch, newQuoteId } = this.props

        // Check if there is a quoteId passed
        // or if a newly created quote exists
        if (id || newQuoteId) {
            return false
        }

        // If a new quote is not created and it is
        // the first file dropped into the dropzone
        // create a quote
        if (dispatch && !newQuoteId && _.findIndex(files, file) === 0) {
            const quoteId = await this.createQuote()

            dispatch(setNewQuoteIdForDropzone({ id: quoteId }))
        }

        return true
    }

    identifyFileTypeAndCreate = (file, files) => {
        const {
            drawingOnly,
            onPreCreate,
            onSinglePdfAttachment,
            isProcessWithoutAttachmentLimitation,
            isPartDfmAttachmentType,
        } = this.props

        if (isBlockedFileName(file.name)) {
            message.error(UPLOAD_BLOCKED_ATTACHMENT_ERROR)
            return false
        }
        // The onSinglePdfAttachment prop enables the FileUploader to only accept pdf files
        // This functionality is used in DrawingSelectionOption in AdditionalRequirements
        if (onSinglePdfAttachment) {
            if (isPdf(file.name) || isProcessWithoutAttachmentLimitation) {
                onSinglePdfAttachment(true, '')
                return this.createAttachment(file)
            }
            onSinglePdfAttachment(false, getFileExtension(file.name))
            return false
        }

        // This is to restrict part DFM report to PPT, with 20MB size limit
        if (isPartDfmAttachmentType) {
            const validExtenstion = files.filter(_file =>
                isValidDfmReportType(_file.name),
            )

            const validSize = files.filter(_file =>
                isValidDfmReportSize(_file.size),
            )

            if (
                validExtenstion.length !== files.length &&
                !isValidDfmReportType(file.name)
            ) {
                message.error(`Please upload PPT or PPTX file.`)
                return false
            } else if (
                validSize.length !== files.length &&
                !isValidDfmReportSize(file.size)
            ) {
                message.error(`Please upload file of size 20MB or less`)
                return false
            } else if (
                validExtenstion.length === files.length &&
                validSize.length === files.length
            ) {
                return this.createAttachment(file)
            }
            return false
        }

        if (onPreCreate) {
            onPreCreate()
        }

        // match against some list of file extensions
        const fileExt = grabAndLowerExt(file)
        const isCAD = CAD_EXTENSIONS.some(cadExt => cadExt === fileExt)
        // function isNativeDrawningName was commented here src/lib/cadExtensions.js, uncomment if it will be required after refactoring.
        //const isNativeDrawing = isNativeDrawingName(file.name)

        // @TODO: This breaks the upload flow. This will be refactored
        // if (isNativeDrawing) {
        //     notification.error({
        //         message: 'Error: Unsupported drawing format',
        //         description:
        //             'We do not accept native drawing file formats at this time. Please re-upload your drawing as a 2D PDF.',
        //     })

        //     return false
        // }

        if (isCAD) {
            file._isCAD = true

            if (drawingOnly) {
                message.error(`Cannot link to CAD models.`)
                return false
            }

            return this.createModel(file)
        }

        return this.createAttachment(file)
    }

    uploadEntity = (...args) => {
        const [file, { onProgress, onSuccess, onError }] = args

        this.setState({
            isUploadingFiles: false,
            isRedirecting: true,
        })
        return uploadItem(file, { onProgress, onSuccess, onError })
    }

    markUploadedEntity = file => {
        const { onUploadSuccess, isPartDfmAttachmentType } = this.props

        if (file._isCAD) {
            return this.markUploadedModel(file)
        }
        const markedUploadedAttachment = this.markUploadedAttachment(file)

        if (typeof onUploadSuccess === 'function' && isPartDfmAttachmentType) {
            return onUploadSuccess(file)
        }

        return markedUploadedAttachment
    }

    createQuote = () => {
        const { dispatch } = this.props

        if (!dispatch) {
            return null
        }

        this.setState({ isCreatingQuote: true })
        return dispatch(createQuote())
    }

    createModel = async file => {
        const {
            dispatch,
            id,
            newQuoteId,
            refetchQuery,
            onError,
            quoteModelId,
            manualRefetch,
            isQueryDetailsQueryRefactorOn,
        } = this.props

        if (!dispatch) {
            return null
        }

        const quoteId = id || newQuoteId

        if (!quoteId) {
            logger.exception(`${CREATE_QUOTE_ERROR_MSG} in createModel`)
            message.error(CREATE_QUOTE_ERROR_MSG)
            throw new Error(CREATE_QUOTE_ERROR_MSG)
        }

        file.referenceId = uuid.v4()

        this.setState({
            isUploadingFiles: true,
            isCreatingQuote: false,
            isRedirecting: false,
        })

        const revisionUploadMetadata = await dispatch(
            createQuoteModelRevision({
                file,
                units: this.state.units,
                quoteModelId: quoteModelId || null,
                quoteId,
                setAsSelectedForQuoteModel: true,
                refetchQuery,
                manualRefetch,
                isQueryDetailsQueryRefactorOn,
            }),
        )

        if (_.isEmpty(revisionUploadMetadata)) {
            // If we failed to create an revisionUploadMetadata don't attempt to upload that one
            // @TODO: figure out how to surface the failed creates to user
            logger.warn('Failed to create model or get uploadUrl', {
                file,
                revisionUploadMetadata,
            })

            if (onError !== undefined) {
                onError(file)
            }

            return false
        }

        Object.assign(file, {
            uploadUrl: revisionUploadMetadata.uploadUrl,
            uploadMetadata: revisionUploadMetadata,
        })

        return file
    }

    markUploadedModel = file => {
        const { dispatch, client } = this.props

        if (typeof dispatch !== 'function') {
            return null
        }

        return client.mutate({
            mutation: finalizeUploadAndProcessPart,
            variables: {
                partId: file.uploadMetadata.partRevision.partId,
            },
        })
    }

    createAttachment = async file => {
        const {
            dispatch,
            id,
            newQuoteId,
            refetchQuery,
            quoteAttachmentId,
            adminReplaced,
            generatedDrawingId,
            isPartDfmAttachmentType,
            userId,
            manualRefetch,
            isQueryDetailsQueryRefactorOn,
        } = this.props

        if (typeof dispatch !== 'function') {
            return null
        }

        if (generatedDrawingId && !quoteAttachmentId) {
            return this.createGeneratedDrawingAttachment(file)
        }

        const quoteId = id || newQuoteId

        if (!quoteId) {
            logger.exception(`${CREATE_QUOTE_ERROR_MSG} in createAttachment`)
            message.error(CREATE_QUOTE_ERROR_MSG)
            throw new Error(CREATE_QUOTE_ERROR_MSG)
        }

        const quoteAttachmentRevision = await dispatch(
            createQuoteAttachmentRevision({
                file,
                quoteId,
                attachmentType: isPartDfmAttachmentType
                    ? 'part-dfm-report'
                    : null,
                quoteAttachmentId: isPartDfmAttachmentType
                    ? null
                    : quoteAttachmentId,
                productAttachmentId: null,
                refetchQuery,
                adminReplaced,
                userId,
                manualRefetch,
                isQueryDetailsQueryRefactorOn,
            }),
        )

        Object.assign(file, {
            quoteAttachmentRevisionId: quoteAttachmentRevision?.id,
            uploadUrl:
                quoteAttachmentRevision?.attachment?.uploadUrl ||
                quoteAttachmentRevision?.asset?.uploadUrl,
        })

        return file
    }

    createGeneratedDrawingAttachment = async file => {
        const { refetchQuery, generatedDrawingId, dispatch } = this.props
        const updatedGeneratedDrawing = await dispatch(
            updateGeneratedDrawing({
                file,
                id: generatedDrawingId,
                refetchQuery,
            }),
        )

        Object.assign(file, {
            generatedDrawingId: updatedGeneratedDrawing.id,
            attachmentId: updatedGeneratedDrawing.attachment.id,
            uploadUrl:
                updatedGeneratedDrawing.attachment.uploadUrl ||
                updatedGeneratedDrawing.asset.uploadUrl,
        })

        return file
    }

    markUploadedAttachment = file => {
        const {
            dispatch,
            id,
            newQuoteId,
            refetchQuery,
            onError,
            generatedDrawingId,
            userId,
            manualRefetch,
            isQueryDetailsQueryRefactorOn,
        } = this.props

        if (typeof dispatch !== 'function') {
            return null
        }

        const quoteId = id || newQuoteId

        if (!quoteId) {
            logger.exception(
                `${CREATE_QUOTE_ERROR_MSG} in markUploadedAttachment`,
            )
            message.error(CREATE_QUOTE_ERROR_MSG)
            throw new Error(CREATE_QUOTE_ERROR_MSG)
        }

        if (generatedDrawingId) {
            return dispatch(
                markUploadedAttachment({
                    file,
                    refetchQuery,
                    quoteId,
                    manualRefetch,
                    isQueryDetailsQueryRefactorOn,
                }),
            ).catch(error => {
                logger.exception(error, {
                    extra: { info: 'Failed to mark attachment uploaded' },
                })

                if (onError !== undefined) {
                    onError(file)
                }

                message.error(`${file.name} failed to complete the upload`)
            })
        }

        return dispatch(
            markQuoteAttachmentRevisionUploaded({
                file,
                quoteId,
                refetchQuery,
                userId,
                manualRefetch,
                isQueryDetailsQueryRefactorOn,
            }),
        ).catch(error => {
            logger.exception(error, {
                extra: { info: 'Failed to mark attachment uploaded' },
            })

            if (onError !== undefined) {
                onError(file)
            }

            message.error(`${file.name} failed to complete the upload`)
        })
    }

    onSuccess = files => {
        const { newQuoteId, dispatch, onSuccess, onSuccessRedirectExactPath } =
            this.props

        if (typeof onSuccess === 'function') {
            return onSuccess(files)
        }

        message.success('All uploads completed')

        if (typeof dispatch !== 'function') {
            return null
        }

        if (newQuoteId) {
            this.props.router.navigate(`/pages/quotes/${newQuoteId}`)
            dispatch(resetQuoteIdForDropzone())
            return null
        }

        if (onSuccessRedirectExactPath) {
            return this.props.router.navigate(onSuccessRedirectExactPath)
        }

        return null
    }

    onError = () => {
        message.error('Failed to upload.')
    }

    onUnitsChange = units => {
        this.setState({ units })
    }

    CustomSpinner = () => {
        const { customSpinner } = this.props

        if (!customSpinner) {
            return null
        }

        return typeof customSpinner === 'function'
            ? customSpinner({
                  isCreatingQuote: this.state.isCreatingQuote,
                  isUploadingFiles: this.state.isUploadingFiles,
                  isRedirecting: this.state.isRedirecting,
              })
            : customSpinner
    }

    render() {
        const {
            dropzone,
            className,
            multiple,
            onChange,
            buttonSpin,
            children,
            boxStyle = {},
        } = this.props

        let isDropzone = false

        if (dropzone) {
            isDropzone = true
        }

        return (
            <Box
                alignItems="center"
                data-test-target="file-uploader-container"
                style={boxStyle}
            >
                <FileUploader
                    placement={UPLOADER_PLACEMENT}
                    onFileDropped={this.checkMeshFiles}
                    onUpload={this.uploadEntity}
                    dropzone={isDropzone}
                    className={className}
                    onBeforeUpload={this.onBeforeUpload}
                    onUploadSuccess={this.markUploadedEntity}
                    onSuccess={this.onSuccess}
                    onError={this.onError}
                    onChange={onChange}
                    showUploadList={false}
                    buttonType="primary"
                    multiple={multiple}
                    buttonSpin={buttonSpin}
                    customSpinner={this.CustomSpinner()}
                    accept={this.props.accept}
                    disabled={!!this.props.disabled}
                >
                    {children}
                </FileUploader>
            </Box>
        )
    }
}

export default _.flowRight(
    customWithRouter,
    withApollo,
    connect(state => {
        const identity = getUserIdentity(state)
        const isQueryDetailsQueryRefactorOn = isFlagEnabled(
            identity,
            PROVISIONED_UI_FLAGS.QUOTE_DETAILS_QUERY_REFACTOR,
            'true',
        )

        return {
            newQuoteId: getNewQuoteIdForDropzone(state),
            isQueryDetailsQueryRefactorOn,
        }
    }),
)(QuoteUploader)
